import { reportError } from "src/lib/observability/reportError";
import { useState, useEffect } from "react";
import { Modal, Input, toast } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { Button } from "src/components/button";
import { modalApi } from "src/infra/modals";
import { FirebaseAPI, TOrder, TOrganization } from "@jsdev_ninja/core";
import { useAppApi } from "src/appApi";
import { useApiState } from "src/appApi/useApiState";
import { FirebaseApi } from "src/lib/firebase";
import { VAT_TYPE } from "src/lib/firebase/api";

type InvoiceDetailsForm = {
	invoiceDate: string;
	customerName: string;
	customerAddress: string;
	allocationNumber: string;
};

function formatTimestampToDateString(timestamp: number): string {
	return new Date(timestamp).toISOString().split("T")[0];
}

/** Returns today's date as YYYY-MM-DD in Israel timezone (Asia/Jerusalem). */
function todayIsrael(): string {
	return Intl.DateTimeFormat("en-CA", {
		timeZone: "Asia/Jerusalem",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(new Date());
}

export function InvoiceDetailsModal({
	selectedOrders,
	onInvoiceCreated,
	initialInvoiceDate,
	linkedDeliveryNote,
	requireAllocation,
}: {
	selectedOrders: TOrder[];
	onInvoiceCreated?: () => void;
	initialInvoiceDate?: number;
	/** When set, this invoice is linked to a specific delivery note. */
	linkedDeliveryNote?: { docUuid: string; number?: string };
	/** When true, an allocation number input is required (invoices >= ₪5,000). */
	requireAllocation?: boolean;
}) {
	const { t } = useTranslation(["common", "admin"]);
	const appApi = useAppApi();
	const { store } = useApiState();
	const [formData, setFormData] = useState<InvoiceDetailsForm>({
		invoiceDate: initialInvoiceDate
			? formatTimestampToDateString(initialInvoiceDate)
			: todayIsrael(),
		customerName: "",
		customerAddress: "",
		allocationNumber: "",
	});
	const [organizations, setOrganizations] = useState<TOrganization[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState<Partial<Record<keyof InvoiceDetailsForm, string>>>({});

	useEffect(() => {
		const loadOrganizations = async () => {
			try {
				const result = await appApi.admin.listOrganizations();
				if (result?.success) {
					setOrganizations(result.data || []);
				}
			} catch (error) {
				reportError("Failed to load organizations:", error);
			}
		};
		loadOrganizations();
	}, []);

	// Get organization from first order to pre-fill name on invoice
	useEffect(() => {
		if (selectedOrders.length > 0 && organizations.length > 0) {
			const firstOrder = selectedOrders[0];
			if (firstOrder.organizationId) {
				const org = organizations.find((o) => o.id === firstOrder.organizationId);
				if (org?.nameOnInvoice) {
					setFormData((prev) => {
						// Only set if customerName is empty
						if (!prev.customerName) {
							return {
								...prev,
								customerName: org.nameOnInvoice || "",
							};
						}
						return prev;
					});
				}
			}
		}
	}, [selectedOrders, organizations]);

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof InvoiceDetailsForm, string>> = {};

		if (!formData.invoiceDate.trim()) {
			newErrors.invoiceDate = t("admin:invoiceDetails.errors.dateRequired");
		}

		if (!formData.customerName.trim()) {
			newErrors.customerName = t("admin:invoiceDetails.errors.nameRequired");
		}

		if (!formData.customerAddress.trim()) {
			newErrors.customerAddress = t("admin:invoiceDetails.errors.addressRequired");
		}

		if (requireAllocation && !formData.allocationNumber.trim()) {
			newErrors.allocationNumber = "נדרש מספר הקצאה לחשבוניות מעל ₪5,000";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm() || !store) {
			return;
		}

		function formatDateDDMMYYYY(input: string) {
			const d = new Date(input);
			const day = String(d.getDate()).padStart(2, "0");
			const month = String(d.getMonth() + 1).padStart(2, "0");
			const year = d.getFullYear();
			return `${day}/${month}/${year}`;
		}

		setIsSubmitting(true);
		try {
			const res = await FirebaseApi.api.createInvoice(store.id, {
				orders: selectedOrders,
				params: {
					item: selectedOrders.map((order) => ({
						details: `תעודת משלוח ${order?.ezDeliveryNote?.doc_number ?? ""}`,
						price: order?.cart.cartTotal ?? 0,
						amount: 1,
						vat_type: VAT_TYPE.NON,
					})),
					transaction_id: crypto.randomUUID(),
					customer_name: formData.customerName,
					customer_email: selectedOrders[0]?.client?.email || "",
					customer_address: formData.customerAddress,
					customer_phone: selectedOrders[0]?.client?.phoneNumber || "",
					description: "ריכוז תעודות משלוח",
					price_total: selectedOrders.reduce(
						(acc, order) => acc + (order?.cart.cartTotal ?? 0),
						0
					),
					parent: linkedDeliveryNote
						? linkedDeliveryNote.docUuid
						: selectedOrders.map((order) => order?.ezDeliveryNote?.doc_uuid).join(","),
					...(requireAllocation && formData.allocationNumber.trim()
						? { allocationNumber: formData.allocationNumber.trim() }
						: {}),
					date: formatDateDDMMYYYY(formData.invoiceDate),
				},
			});

			if (!res.success) {
				toast.danger("שגיאה ביצירת חשבונית", {
					description:
						(res as any).message ?? (res as any).error ?? "אירעה שגיאה בלתי צפויה",
				});
				return;
			}

			if (!res.data) {
				toast.danger("שגיאה ביצירת חשבונית", {
					description: "לא התקבלו נתוני חשבונית מהשרת",
				});
				return;
			}

			if (res.success) {
				onInvoiceCreated?.();
				// update orders with invoice data
				// TODO: should be handle in backend
				await Promise.all(
					selectedOrders.map(async (order) => {
						await FirebaseApi.firestore.setV2<{ id: string; doc: TOrder }>({
							collection: FirebaseAPI.firestore.getPath({
								companyId: store.companyId,
								storeId: store.id,
								collectionName: "orders",
							}),
							doc: {
								id: order.id,
								invoice: {
									...(res.data as any),
									date: new Date(formData.invoiceDate).getTime(),
								},
							} as any,
						});
					})
				);
				modalApi.closeModal("invoiceDetails");
			}
		} catch (error) {
			reportError("Failed to create invoice:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleChange = (field: keyof InvoiceDetailsForm, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
		// Clear error for this field when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: undefined,
			}));
		}
	};

	return (
		<Modal.Backdrop
			isOpen
			onOpenChange={(open) => {
				if (!open) modalApi.closeModal("invoiceDetails");
			}}
		>
			<Modal.Container size="md" scroll="inside">
				<Modal.Dialog>
					<Modal.Header>
						<Modal.Heading>
							<div className="text-start">{t("admin:invoiceDetails.title")}</div>
							<div className="text-sm text-default-500 text-start">
								{t("admin:invoiceDetails.description", { count: selectedOrders.length })}
							</div>
						</Modal.Heading>
					</Modal.Header>
					<Modal.Body>
						<div className="space-y-4">
							{linkedDeliveryNote && (
								<div
									className="flex items-center gap-2 rounded-lg border border-[var(--primary)] bg-[color-mix(in_oklab,var(--primary)_10%,transparent)] px-3 py-2 text-sm font-medium text-[var(--primary)] text-start"
									dir="rtl"
								>
									מקושר לתעודת משלוח {linkedDeliveryNote.number ?? linkedDeliveryNote.docUuid.slice(0, 8)}
								</div>
							)}
							<div className="flex flex-col gap-1">
								<label className="block text-sm font-medium text-start">
									{t("admin:invoiceDetails.invoiceDate")}
									<span className="text-danger ml-1">*</span>
								</label>
								<Input
									type="date"
									value={formData.invoiceDate}
									onChange={(e) => handleChange("invoiceDate", e.target.value)}
									className="text-start"
								/>
								{errors.invoiceDate && (
									<p className="text-sm text-danger text-start">{errors.invoiceDate}</p>
								)}
							</div>
							<div className="flex flex-col gap-1">
								<label className="block text-sm font-medium text-start">
									{t("admin:invoiceDetails.customerName")}
									<span className="text-danger ml-1">*</span>
								</label>
								<Input
									placeholder={t("admin:invoiceDetails.customerNamePlaceholder")}
									value={formData.customerName}
									onChange={(e) => handleChange("customerName", e.target.value)}
									className="text-start"
								/>
								{errors.customerName && (
									<p className="text-sm text-danger text-start">{errors.customerName}</p>
								)}
							</div>
							<div className="flex flex-col gap-1">
								<label className="block text-sm font-medium text-start">
									{t("admin:invoiceDetails.customerAddress")}
									<span className="text-danger ml-1">*</span>
								</label>
								<Input
									placeholder={t("admin:invoiceDetails.customerAddressPlaceholder")}
									value={formData.customerAddress}
									onChange={(e) => handleChange("customerAddress", e.target.value)}
									className="text-start"
								/>
								{errors.customerAddress && (
									<p className="text-sm text-danger text-start">
										{errors.customerAddress}
									</p>
								)}
							</div>
							{requireAllocation && (
								<div className="flex flex-col gap-1">
									<label className="block text-sm font-medium text-start" dir="rtl">
										מספר הקצאה (חשבונית ישראל)
										<span className="text-danger ml-1">*</span>
									</label>
									<Input
										type="number"
										inputMode="numeric"
										placeholder="9 ספרות"
										value={formData.allocationNumber}
										onChange={(e) => handleChange("allocationNumber", e.target.value)}
										className="text-start"
									/>
									{errors.allocationNumber && (
										<p className="text-sm text-danger text-start">{errors.allocationNumber}</p>
									)}
								</div>
							)}
						</div>
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="danger"
							onPress={() => modalApi.closeModal("invoiceDetails")}
							isDisabled={isSubmitting}
						>
							{t("common:cancel")}
						</Button>
						<Button variant="primary" onPress={handleSubmit} isPending={isSubmitting}>
							{t("admin:invoiceDetails.createInvoice")}
						</Button>
					</Modal.Footer>
				</Modal.Dialog>
			</Modal.Container>
		</Modal.Backdrop>
	);
}
